import { http } from "./http";
import { callNative, inNativeApp, isIOS } from "../lib/native";

export type PushState = "unsupported" | "needs-install" | "denied" | "off" | "on";

const isStandalone = () => window.matchMedia("(display-mode: standalone)").matches || (navigator as { standalone?: boolean }).standalone === true;

function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, "+").replace(/_/g, "/"));
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

async function registration(): Promise<ServiceWorkerRegistration> {
  return (await navigator.serviceWorker.getRegistration()) ?? navigator.serviceWorker.register("/sw.js");
}

/*
 * JustOne 앱 안: 알림 권한과 기기 토큰은 앱이 갖고, 서버에는 토큰만 올린다 (/api/push/apns).
 * 이 기기에서 끄면 기억해 두고, 앱이 권한을 갖고 있어도 다시 올리지 않는다.
 */
interface AppPush {
  status: "granted" | "denied" | "notDetermined";
  token: string | null;
}
const APP_OFF = "justone.appPushOff";
function appOff() {
  try {
    return localStorage.getItem(APP_OFF) === "1";
  } catch {
    return false;
  }
}
function setAppOff(off: boolean) {
  try {
    if (off) localStorage.setItem(APP_OFF, "1");
    else localStorage.removeItem(APP_OFF);
  } catch {}
}

/** 로그인한 뒤: 앱이 알림을 허락받았으면 기기 토큰을 서버에 올린다 (토큰은 바뀔 수 있다) */
export async function syncAppPush() {
  if (!inNativeApp() || appOff()) return;
  const s = await callNative<AppPush>("push.state");
  if (s.status === "granted" && s.token) await http.post("/api/push/apns", { token: s.token });
}

/** 로그아웃 전에: 이 기기로 그 계정의 알림이 더 오지 않게 */
export async function forgetAppPush() {
  if (!inNativeApp()) return;
  const s = await callNative<AppPush>("push.state");
  if (s.token) await http.req("DELETE", "/api/push/apns", { token: s.token });
}

/** 이 기기의 푸시 상태 */
export async function pushState(): Promise<PushState> {
  if (inNativeApp()) {
    const s = await callNative<AppPush>("push.state");
    if (s.status === "denied") return "denied";
    return s.status === "granted" && s.token && !appOff() ? "on" : "off";
  }
  if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) {
    return isIOS() && !isStandalone() ? "needs-install" : "unsupported";
  }
  if (Notification.permission === "denied") return "denied";
  const sub = await (await registration()).pushManager.getSubscription();
  return sub ? "on" : "off";
}

/** 알림 권한을 묻고 이 기기를 구독한다 (앱 안에서는 공개 키가 필요 없다) */
export async function enablePush(publicKey: string | null): Promise<PushState> {
  if (inNativeApp()) {
    const s = await callNative<AppPush>("push.enable");
    if (s.status === "denied") return "denied";
    if (!s.token) throw new Error("알림을 켜지 못했습니다. 인터넷 연결을 확인하고 다시 시도하세요.");
    await http.post("/api/push/apns", { token: s.token });
    setAppOff(false);
    return "on";
  }
  if (!publicKey) return "unsupported";
  const permission = await Notification.requestPermission();
  if (permission !== "granted") return permission === "denied" ? "denied" : "off";
  const reg = await registration();
  await navigator.serviceWorker.ready;
  const sub =
    (await reg.pushManager.getSubscription()) ??
    (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(publicKey) }));
  await http.post("/api/push/subscriptions", sub.toJSON());
  return "on";
}

export async function disablePush(): Promise<PushState> {
  if (inNativeApp()) {
    await forgetAppPush();
    setAppOff(true);
    return "off";
  }
  const sub = await (await registration()).pushManager.getSubscription();
  if (sub) {
    await http.req("DELETE", "/api/push/subscriptions", { endpoint: sub.endpoint }).catch(() => {});
    await sub.unsubscribe();
  }
  return "off";
}
